// ─── Project Artifacts Client (ocn-S241-P0, seam extended in ocn-S246-P0) ──
// Typed fetch wrappers for the scaffolding docs endpoints.
// Auth follows the same pattern as api.ts (Supabase JWT bearer).
//
// GET /api/projects/:id/artifacts
// GET /api/projects/:id/artifacts/:key
//
// Server-side search seam (ocn-S246-P0):
//   listProjectArtifacts accepts optional { q, type } params.
//   Currently the API route does NOT process these (filtering is client-side
//   in ArtifactsPanel). When the route is extended to support server-side
//   search/filtering, pass the params here and remove client-side filtering
//   from ArtifactsPanel — no other caller changes needed.

import { authHeaders, sessionExpiredBus } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ─── Types ───────────────────────────────────────────────────────

export interface ArtifactMeta {
  key: string;
  friendly_title: string;
  source: string;
  updated_at: string | null;
}

export interface ArtifactContent {
  key: string;
  friendly_title: string;
  content: string | null;
  source: string | null;
  updated_at: string | null;
}

/** Optional search/filter params for listProjectArtifacts. */
export interface ArtifactListParams {
  /**
   * Free-text query. When the server-side seam is activated, the route
   * will search artifact titles and content. Currently unused server-side;
   * ArtifactsPanel filters client-side over the full returned list.
   */
  q?: string;
  /**
   * Artifact key to filter by (e.g. 'architecture', 'worldview').
   * Server-side seam: pass to the route once it supports this param.
   * Currently unused server-side; ArtifactsPanel filters client-side.
   */
  type?: string;
}

// ─── Fetch helpers ───────────────────────────────────────────────

async function getJson<T>(path: string): Promise<T> {
  if (sessionExpiredBus.expired) {
    throw new Error('Session expired — please sign in again');
  }
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error ?? `API error: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Public API ──────────────────────────────────────────────────

/**
 * List available scaffolding artifacts for a project.
 *
 * @param projectId - the project to list artifacts for
 * @param _params   - optional search/filter hints (server-side seam; currently
 *                    unused — ArtifactsPanel applies these client-side over the
 *                    full list returned by this call).
 */
export function listProjectArtifacts(
  projectId: string,
  _params?: ArtifactListParams,
): Promise<{
  artifacts: ArtifactMeta[];
  project_id: string;
}> {
  // TODO(server-side-search): when the GET /api/projects/:id/artifacts route
  // supports ?q=&type=, build a URLSearchParams from _params and append here.
  return getJson(`/api/projects/${encodeURIComponent(projectId)}/artifacts`);
}

/** Fetch the markdown content of a specific artifact by key. */
export function getProjectArtifact(
  projectId: string,
  key: string,
): Promise<ArtifactContent> {
  return getJson(
    `/api/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(key)}`,
  );
}
